import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  TbReceipt,
  TbUser,
  TbArrowsExchange,
  TbArrowRight,
  TbTrendingUp,
  TbTrendingDown,
} from 'react-icons/tb';
import Button from '../buttons/Button';
import * as transactionProcessingService from '../../../services/transactionProcessingService';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import * as bankAccountService from '../../../services/bankAccountService';
import { TransactionType } from '../../../types/bankAccount';

interface TransactionProcessingCardProps {
  onDataReload?: () => void;
}

function TransactionProcessingCard({ onDataReload }: TransactionProcessingCardProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.overview.transactionProcessing' });
  const navigate = useNavigate();
  const business = useSelectedBusiness();
  const [processingAll, setProcessingAll] = useState<'idle' | 'starting' | 'processing'>('idle');
  const [loading, setLoading] = useState<boolean>(false);
  const [summary, setSummary] = useState<{
    processingStatus: number;
    processedCount: number;
    totalCount: number;
    unprocessedCount: number;
    breakdown: {
      groupIncome: number;
      groupExpenses: number;
      personalIncome: number;
      personalExpenses: number;
      contributions: number;
      internalTransfers: number;
    };
  } | null>(null);

  const hasNoData = useMemo(() => {
    if (loading || !summary) return false;
    return summary.processedCount === 0 && summary.unprocessedCount === 0;
  }, [loading, summary]);

  const loadSummary = useCallback(async () => {
    if (!business || business.businessId == null) return;
    try {
      setLoading(true);
      const [accounts, transactions] = await Promise.all([
        bankAccountService.getAllBankAccounts(),
        bankAccountService.getAllTransactions(),
      ]);

      // Count unprocessed mutations (mutations not linked to any created bank transaction)
      const mutationCounts = await Promise.all(
        accounts.map(async (acc) => {
          try {
            const mutations = await bankAccountService.getAllMutationsForBank(acc.accountId);
            return mutations.filter((m) => m.bankTransactionCount === 0).length;
          } catch (e) {
            console.error('Error fetching mutations for account', acc.accountId, e);
            return 0;
          }
        }),
      );
      const unprocessedCount = mutationCounts.reduce((sum, c) => sum + c, 0);

      const processedCount = transactions.length;
      const totalCount = processedCount + unprocessedCount;
      const processingStatus = totalCount > 0 ? Math.round((processedCount / totalCount) * 100) : 0;

      // Derive breakdown from processed transactions by TransactionType
      const breakdown = transactions.reduce(
        (acc, txn) => {
          switch (txn.transactionType) {
            case TransactionType.GROUP_INCOME:
              acc.groupIncome += 1;
              break;
            case TransactionType.GROUP_EXPENSE:
              acc.groupExpenses += 1;
              break;
            case TransactionType.PERSONAL_INCOME:
              acc.personalIncome += 1;
              break;
            case TransactionType.CONTRIBUTION:
              acc.contributions += 1;
              break;
            case TransactionType.INTERNAL_BANK_TRANSFER:
              acc.internalTransfers += 1;
              break;
            case TransactionType.PERSONAL_EXPENSE:
              acc.personalExpenses += 1;
              break;
            default:
              break;
          }
          return acc;
        },
        {
          groupIncome: 0,
          groupExpenses: 0,
          personalIncome: 0,
          personalExpenses: 0,
          contributions: 0,
          internalTransfers: 0,
        },
      );

      setSummary({
        processingStatus,
        processedCount,
        totalCount,
        unprocessedCount,
        breakdown,
      });
    } catch (error) {
      console.error('Error loading transaction processing summary:', error);
      setSummary({
        processingStatus: 0,
        processedCount: 0,
        totalCount: 0,
        unprocessedCount: 0,
        breakdown: {
          groupIncome: 0,
          groupExpenses: 0,
          personalIncome: 0,
          personalExpenses: 0,
          contributions: 0,
          internalTransfers: 0,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [business?.businessId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const triggerProcessAll = async () => {
    if (!business || business.businessId == null) return;
    try {
      setProcessingAll('starting');
      await transactionProcessingService.processMutations({
        businessId: business.businessId as number,
      });
      setProcessingAll('processing');
      toast.success(t('toast.processingStarted'));
      setTimeout(() => {
        onDataReload?.();
        loadSummary();
        setProcessingAll('idle');
      }, 2000);
    } catch (error) {
      console.error('Error processing mutations:', error);
      toast.error(t('toast.processingFailed'));
      setProcessingAll('idle');
    }
  };

  const data = summary ?? {
    processingStatus: 0,
    processedCount: 0,
    totalCount: 0,
    unprocessedCount: 0,
    breakdown: {
      groupIncome: 0,
      groupExpenses: 0,
      personalIncome: 0,
      personalExpenses: 0,
      contributions: 0,
      internalTransfers: 0,
    },
  };

  return (
    <div className="mt-8">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t('title')}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() =>
              navigate('/financial/administration', { state: { activeTab: 'transactions' } })
            }
            className="flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium text-primary transition-colors duration-200 hover:bg-primary/5">
            <span>{t('seeAll')}</span>
            <TbArrowRight size={16} />
          </button>
        </div>

        {hasNoData ? (
          <div className="flex items-center justify-between rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {t('empty.title', { defaultValue: 'No transactions or mutations yet' })}
              </p>
              <p className="text-xs text-gray-500">
                {t('empty.subtitle', {
                  defaultValue:
                    'Connect a bank account and sync transactions to see processing progress.',
                })}
              </p>
            </div>
            <div>
              <Button
                variant="primary"
                onClick={() =>
                  navigate('/financial/administration', { state: { activeTab: 'bank-accounts' } })
                }>
                {t('empty.cta', { defaultValue: 'Connect account' })}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Processing Status */}
            <div className="space-y-4">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{t('processingStatus')}</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {loading ? '—' : `${data.processingStatus}%`}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-200">
                  <div
                    className="h-2 rounded-full bg-green-500"
                    style={{ width: `${data.processingStatus}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {loading
                    ? '—'
                    : `${data.processedCount} of ${data.totalCount} mutations processed`}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                  <span className="text-sm text-gray-600">{t('processed')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-3 w-3 rounded-full bg-orange-400" />
                  <span className="text-sm text-gray-600">{t('unprocessed')}</span>
                </div>
              </div>

              {/* Process All Button - Only show when there are unprocessed mutations */}
              {data.unprocessedCount > 0 && (
                <div className="pt-2">
                  <Button
                    variant="primary-dark"
                    onClick={triggerProcessAll}
                    disabled={processingAll !== 'idle'}>
                    {processingAll === 'processing' || processingAll === 'starting'
                      ? t('actions.processing')
                      : t('actions.processAll')}
                  </Button>
                </div>
              )}
            </div>

            {/* Transaction Breakdown */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">{t('breakdown')}</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TbTrendingUp className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-600">
                      {t('groupIncome', { defaultValue: 'Group income' })}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {loading ? '—' : data.breakdown.groupIncome}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TbTrendingDown className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-gray-600">
                      {t('groupExpenses', { defaultValue: 'Group expenses' })}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {loading ? '—' : data.breakdown.groupExpenses}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TbUser className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-gray-600">
                      {t('personalIncome', { defaultValue: 'Personal income' })}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {loading ? '—' : data.breakdown.personalIncome}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TbUser className="h-4 w-4 text-purple-500" />
                    <span className="text-sm text-gray-600">{t('personalExpenses')}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {loading ? '—' : data.breakdown.personalExpenses}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TbReceipt className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-gray-600">
                      {t('contributions', { defaultValue: 'Contributions' })}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {loading ? '—' : data.breakdown.contributions}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TbArrowsExchange className="h-4 w-4 text-orange-500" />
                    <span className="text-sm text-gray-600">{t('internalTransfers')}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {loading ? '—' : data.breakdown.internalTransfers}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TransactionProcessingCard;
